import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

import { tokens } from "../../theme";

/* ---------- helpers ---------- */

const show = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v);

const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

const normalizeList = (list, nameKeys = ["name", "fullName", "title"]) => {
  if (!Array.isArray(list)) return [];

  return list
    .map((o) => {
      const id = o?.id ?? o?.ID ?? o?.Id ?? o?.roleId ?? o?.uuid;

      const nameKey = nameKeys.find((k) => o && o[k] != null);

      const name =
        o?.[nameKey] ?? o?.label ?? o?.code ?? `#${id ?? "?"}`;

      return { id, name };
    })
    .filter((o) => o.id !== undefined && o.id !== null);
};

function ensureOption(options, id) {
  if (id === "" || id === null || id === undefined) return options || [];

  const nid = Number(id);

  const exists = (options || []).some(
    (o) => Number(o.id) === nid
  );

  return exists
    ? options
    : [{ id: nid, name: `#${nid}` }, ...(options || [])];
}

const ACTION_OPTIONS = ["create", "read", "update", "delete"];

/* ---------- parse actions ---------- */

const parseActions = (actions) => {
  try {
    if (!actions) return {};

    return typeof actions === "string"
      ? JSON.parse(actions)
      : actions;
  } catch {
    return {};
  }
};

/* ---------- API ---------- */

const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });

    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";

    return { data: null, error: msg };
  }
}

async function putJSON(path, body) {
  try {
    const res = await api.put(path, body);

    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;

    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);

    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;

    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */

export default function ViewPermission() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);

  const [editRow, setEditRow] = React.useState(null);

  const [editValues, setEditValues] = React.useState({
    module: "",
    roleId: "",
    actions: {
      create: false,
      read: false,
      update: false,
      delete: false,
    },
  });

  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);

  const [deleting, setDeleting] = React.useState(false);

  const [roles, setRoles] = React.useState([]);

  const [optsLoading, setOptsLoading] = React.useState(false);

  /* ---------- load permissions ---------- */

  const load = React.useCallback(async () => {
    setLoading(true);

    const { data, error } = await getJSON("/api/permission");

    setLoading(false);

    if (error) {
      setToast({
        open: true,
        msg: "Failed to load permissions",
        sev: "error",
      });

      setRows([]);

      return;
    }

    setRows(Array.isArray(data?.data) ? data.data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  /* ---------- load roles ---------- */

  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);

    const roleRes = await getJSON("/api/role");

    setOptsLoading(false);

    setRoles(normalizeList(roleRes?.data?.data || []));
  }, []);

  React.useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const roleNameById = React.useMemo(
    () =>
      Object.fromEntries(
        (roles || []).map((r) => [Number(r.id), r.name])
      ),
    [roles]
  );

  /* ---------- columns ---------- */

  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,

      valueGetter: (params) => {
        const visibleIndex =
          params.api.getRowIndexRelativeToVisibleRows(
            params.id
          );

        const pm =
          params.api?.state?.pagination?.paginationModel || {
            page: 0,
            pageSize: rows.length || 0,
          };

        const pageOffset =
          (pm.page || 0) * (pm.pageSize || 0);

        return pageOffset + visibleIndex + 1;
      },
    },

    {
      field: "module",
      headerName: "Module",
      flex: 1,
      minWidth: 180,
    },

    {
      field: "actionsList",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,

      valueGetter: (params) => {
        const actions = parseActions(params.row.actions);

        const enabled = Object.entries(actions)
          .filter(([_, value]) => value === true)
          .map(([key]) => key);

        return enabled.length
          ? enabled.join(", ")
          : "-";
      },
    },

    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 180,

      valueGetter: ({ row }) =>
        row?.role?.name ??
        roleNameById[Number(row?.roleId)] ??
        "-",
    },

    {
      field: "actions",
      headerName: "Manage",
      width: 180,

      sortable: false,
      filterable: false,

      renderCell: (params) => {
        const row = params.row;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton
                size="small"
                onClick={() => setDetailRow(row)}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={async () => {
                  setEditRow(row);

                  setEditValues({
                    module: row.module ?? "",
                    roleId: toNum(row.roleId),

                    actions: parseActions(row.actions),
                  });

                  await loadOptions();

                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  /* ---------- styles ---------- */

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },

    "& .MuiDataGrid-cell": {
      borderBottom: "none",
    },

    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },

    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },

    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
  };

  /* ---------- update ---------- */

  const handleUpdate = async () => {
    if (!editRow?.id) return;

    setSaving(true);

    const payload = {
      module: String(editValues.module ?? "").trim(),

      actions: {
        create: !!editValues.actions?.create,
        read: !!editValues.actions?.read,
        update: !!editValues.actions?.update,
        delete: !!editValues.actions?.delete,
      },

      roleId: Number(editValues.roleId),
    };

    const { data, error } = await putJSON(
      `/api/permission/${editRow.id}`,
      payload
    );

    setSaving(false);

    if (error) {
      setToast({
        open: true,
        msg: "Update failed",
        sev: "error",
      });

      return;
    }

    setToast({
      open: true,
      msg: "Permission updated successfully",
      sev: "success",
    });

    setEditOpen(false);

    setEditRow(null);

    await Promise.all([load(), loadOptions()]);
  };

  /* ---------- delete ---------- */

  const handleDelete = async () => {
    if (!deleteRow?.id) return;

    setDeleting(true);

    const { data, error } = await deleteJSON(
      `/api/permission/${deleteRow.id}`
    );

    setDeleting(false);

    if (error) {
      setToast({
        open: true,
        msg: "Delete failed",
        sev: "error",
      });

      return;
    }

    setToast({
      open: true,
      msg:
        data?.message ||
        "Permission deleted successfully",
      sev: "success",
    });

    setDeleteRow(null);

    await Promise.all([load(), loadOptions()]);
  };

  /* ---------- toggle action ---------- */

  const toggleAction = (action) => {
    setEditValues((s) => ({
      ...s,

      actions: {
        ...s.actions,
        [action]: !s.actions?.[action],
      },
    }));
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="PERMISSIONS"
          subtitle="View, edit, and manage permissions"
        />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* DETAILS */}

      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Permission Details
        </DialogTitle>

        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Module"
                  value={show(detailRow.module)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Role"
                  value={
                    detailRow?.role?.name ??
                    roleNameById[
                      Number(detailRow?.roleId)
                    ] ??
                    "-"
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <LabelValue
                  label="Actions"
                  value={(() => {
                    const actions = parseActions(
                      detailRow.actions
                    );

                    const enabled = Object.entries(
                      actions
                    )
                      .filter(([_, v]) => v)
                      .map(([k]) => k);

                    return enabled.length
                      ? enabled.join(", ")
                      : "-";
                  })()}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDetailRow(null)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT */}

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Permission</DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              label="Module"
              value={editValues.module}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  module: e.target.value,
                }))
              }
              fullWidth
              variant="filled"
            />

            <TextField
              select
              label="Role"
              value={editValues.roleId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  roleId: toNum(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh roles">
                      <IconButton
                        size="small"
                        onClick={loadOptions}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(
                roles,
                editValues.roleId
              ).map((r) => (
                <MenuItem
                  key={r.id}
                  value={Number(r.id)}
                >
                  {r.name}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Actions
              </Typography>

              <FormGroup row>
                {ACTION_OPTIONS.map((act) => (
                  <FormControlLabel
                    key={act}
                    control={
                      <Checkbox
                        checked={
                          !!editValues.actions?.[act]
                        }
                        onChange={() =>
                          toggleAction(act)
                        }
                      />
                    }
                    label={act}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setEditOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE */}

      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Permission
        </DialogTitle>

        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete it?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteRow(null)}
            disabled={deleting}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TOAST */}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() =>
          setToast((s) => ({
            ...s,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() =>
            setToast((s) => ({
              ...s,
              open: false,
            }))
          }
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */

function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        sx={{ fontWeight: 600 }}
      >
        {value ?? "-"}
      </Typography>

      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}